/**
 * @file ページで読むときの、問 ↔ ページ ↔ 目次 ↔ 別冊の対応 (純関数)。
 *
 * @what: 問の出典 (`p.020`) からページ番号を取り出す、ページに載る問を引く、ページがどの
 *   見出し (学校) の中かを目次から決める、別冊 (companion_of) の目次から見出しの一致で
 *   ページを引く。
 * @why: 問題ごとの切り出しとページ画像は同じ資料の 2 つの見せ方で、行き来できてはじめて
 *   「両方あれば切り替える」(epic_23 §8) になる。別冊の解答例は学校ごとにページが
 *   決まっているので、見出し (学校名) を鍵に資料をまたいで引ける。
 * @failure: 例外を投げない。出典にページ番号が無い問はページに結びつかない (リーダーでは
 *   読める)。見出しが一致しない別冊はページに結びつかない (別冊の先頭は開ける)。
 */

import type { StudyPage } from "../api/pages";
import type { StudyScanItem } from "../api/scans";
import type { StudySource, StudySourceTocEntry } from "../api/sources";

/**
 * 出典 (`… p.020`) の末尾から紙に刷られたページ番号を取り出す。無ければ null。
 *
 * @why: 出典は取り込みが本の記載から書き起こす引用なので、値は刷られた番号である
 *   (取り込みの規約では `pages/` の連番 = 刷られた番号に揃えるのを原則とし、表紙や扉を
 *   前に付けた資料だけ差が出る)。ページ画像・メモ・AI の欄・復習カードはどれもリーダーの
 *   連番で引くので、この値を使う側が `readerPageOf` で連番へ直す。
 */
export function pageFromSourceRef(index: Record<string, string>): number | null {
  const m = /p\.(\d{1,4})\s*$/.exec(index["出典"]?.trim() ?? "");
  if (!m) return null;
  const page = Number(m[1]);
  return page >= 1 ? page : null;
}

/** そのページに載る問 (出典のページ番号が一致するもの、ページ順のまま)。page は刷られた番号で渡す。 */
export function itemsOnPage(items: readonly StudyScanItem[], page: number): StudyScanItem[] {
  return items.filter((i) => pageFromSourceRef(i.index) === page);
}

/** ページの一覧に印を付けるために、出典のページ番号 (刷られた番号) ごとの問の数を一度だけ数える。 */
export function itemCountByPage(items: readonly StudyScanItem[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const i of items) {
    const page = pageFromSourceRef(i.index);
    if (page === null) continue;
    counts.set(page, (counts.get(page) ?? 0) + 1);
  }
  return counts;
}

/**
 * ページを含む見出し。開始ページが page 以下で最後の「開始ページの値」を持つ行すべて
 * (同じページから始まる見出しが複数あれば全部)。目次が無い、または最初の見出しより前の
 * ページなら空。
 */
export function tocEntriesAt(
  toc: readonly StudySourceTocEntry[] | undefined,
  page: number,
): StudySourceTocEntry[] {
  if (!toc || toc.length === 0) return [];
  let start: number | null = null;
  for (const e of toc) {
    if (e.page <= page) start = e.page;
    else break;
  }
  if (start === null) return [];
  return toc.filter((e) => e.page === start);
}

/** 別冊 (companion_of が sourceId の資料)。 */
export function companionsOf(sources: readonly StudySource[], sourceId: string): StudySource[] {
  return sources.filter((s) => s.companion_of === sourceId);
}

/** 別冊の目次で見出しが一致する行の開始ページ。無ければ null。 */
export function companionPageFor(companion: StudySource, title: string): number | null {
  const wanted = title.trim();
  if (!wanted) return null;
  const hit = companion.toc?.find((e) => e.title.trim() === wanted);
  return hit ? hit.page : null;
}

/** 一覧の中で、指定のページ以上で最初のもの (無ければ最後)。一覧が空なら null。 */
export function nearestPage(pages: readonly StudyPage[], page: number): StudyPage | null {
  if (pages.length === 0) return null;
  return pages.find((p) => p.page >= page) ?? pages[pages.length - 1]!;
}

/**
 * 紙に刷られたページ番号を、リーダーの連番から資料の差を引いて出す。
 *
 * @what: 本だなが持つページ (資料のページ・学びメモ・復習カード・ふりかえり) はすべて
 *   リーダーの連番で、人へ見せるときだけこの関数で刷られた番号に直す。
 * @why: 自炊でカバーや扉をどこまで含めたかで差が決まるので、保存する値は連番に寄せ、
 *   換算は表示の側だけで行う。共通データ (問題一覧・解説) だけが刷られた番号を持ち、
 *   そちらは逆向きに `readerPageOf` で連番へ直す。
 */
export function printedPageOf(source: Pick<StudySource, "reader_offset">, readerPage: number): number {
  return readerPage - (source.reader_offset ?? 0);
}

/** 共通データが持つ「刷られた番号」を、リーダーの連番に直す。 */
export function readerPageOf(source: Pick<StudySource, "reader_offset">, printedPage: number): number {
  return printedPage + (source.reader_offset ?? 0);
}
